<template>
  <div class="committees-page">
    <div class="page-header">
      <h1>Congressional Committees</h1>
      <div class="header-controls">
        <div class="congress-selector">
          <label for="congress">Period of Congress:</label>
          <Select 
            id="congress" 
            v-model="selectedCongress" 
            :options="congressOptions"
            optionLabel="label"
            optionValue="value"
            @change="loadCommittees"
          />
        </div>
        <Button 
          label="Add Committee" 
          icon="pi pi-plus" 
          @click="openAddModal"
        />
      </div>
    </div>

    <div class="chamber-tabs">
      <Button 
        :label="`Senate Committees (${senateCount})`"
        :severity="selectedChamber === 'Senate' ? 'primary' : 'secondary'"
        @click="selectChamber('Senate')"
      />
      <Button 
        :label="`House Committees (${houseCount})`"
        :severity="selectedChamber === 'House' ? 'primary' : 'secondary'"
        @click="selectChamber('House')"
      />
    </div>

    <DataTable 
      :value="filteredCommittees" 
      :paginator="true" 
      :rows="10"
      :rowsPerPageOptions="[5, 10, 20, 50]"
      paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
      currentPageReportTemplate="{first} to {last} of {totalRecords}"
      :loading="loading"
      stripedRows
      showGridlines
      responsiveLayout="scroll"
      :globalFilterFields="['name', 'jurisdiction', 'chairperson']"
      v-model:filters="filters"
      filterDisplay="menu"
    >
      <template #header>
        <div class="table-header">
          <h3>{{ selectedChamber }} Committees</h3>
          <IconField iconPosition="left">
            <InputIcon class="pi pi-search" />
            <InputText v-model="filters['global'].value" placeholder="Search committees..." />
          </IconField>
        </div>
      </template>
      <Column field="name" header="Committee Name" sortable :showFilterMatchModes="false">
        <template #body="{ data }">
          <span class="committee-name">{{ data.name }}</span>
        </template>
        <template #filter="{ filterModel }">
          <InputText v-model="filterModel.value" type="text" placeholder="Search by name" />
        </template>
      </Column>
      <Column field="jurisdiction" header="Jurisdiction" sortable :showFilterMatchModes="false">
        <template #filter="{ filterModel }">
          <InputText v-model="filterModel.value" type="text" placeholder="Search jurisdiction" />
        </template>
      </Column>
      <Column field="chairperson" header="Chairperson" sortable :showFilterMatchModes="false">
        <template #filter="{ filterModel }">
          <InputText v-model="filterModel.value" type="text" placeholder="Search chairperson" />
        </template>
      </Column>
      <Column header="Actions" :exportable="false">
        <template #body="{ data }">
          <div class="actions">
            <Button 
              icon="pi pi-pencil" 
              severity="info" 
              size="small"
              @click="openEditModal(data)" 
            />
            <Button 
              icon="pi pi-trash" 
              severity="danger" 
              size="small"
              @click="deleteCommittee(data.id)" 
            />
          </div>
        </template>
      </Column>
    </DataTable>

    <Dialog 
      v-model:visible="showModal" 
      :header="isEditing ? 'Edit Committee' : 'Add Committee'" 
      :modal="true" 
      :closable="false" 
      :dismissableMask="false"
      :style="{width: '500px'}"
    >
      <form @submit.prevent="saveCommittee" class="modal-form">
        <div class="form-grid">
          <div class="form-group">
            <IftaLabel>
              <InputText 
                id="name"
                v-model="form.name" 
                :class="{ 'p-invalid': !form.name && formSubmitted }"
                class="w-full"
                required 
              />
              <label for="name">Committee Name *</label>
            </IftaLabel>
          </div>
          
          <div class="form-group">
            <IftaLabel>
              <Textarea 
                id="jurisdiction"
                v-model="form.jurisdiction" 
                rows="3"
                class="w-full" 
              />
              <label for="jurisdiction">Jurisdiction</label>
            </IftaLabel>
          </div>
          
          <div class="form-group">
            <IftaLabel>
              <InputText 
                id="chairperson"
                v-model="form.chairperson"
                class="w-full" 
              />
              <label for="chairperson">Chairperson</label>
            </IftaLabel>
          </div>
          
          <div class="form-group">
            <IftaLabel>
              <Select 
                id="chamber"
                v-model="form.chamber" 
                :options="chamberOptions"
                optionLabel="label"
                optionValue="value"
                :class="{ 'p-invalid': !form.chamber && formSubmitted }"
                class="w-full"
                required 
              />
              <label for="chamber">Chamber *</label>
            </IftaLabel>
          </div>
        </div>
        
        <div class="modal-actions">
          <Button 
            type="button" 
            label="Cancel" 
            severity="secondary" 
            icon="pi pi-times"
            @click="closeModal" 
          />
          <Button 
            type="submit" 
            :label="isEditing ? 'Update Committee' : 'Create Committee'" 
            icon="pi pi-check"
            :loading="saving"
          />
        </div>
      </form>
    </Dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'
import InputText from 'primevue/inputtext'
import Textarea from 'primevue/textarea'
import Select from 'primevue/select'
import IconField from 'primevue/iconfield'
import InputIcon from 'primevue/inputicon'
import IftaLabel from 'primevue/iftalabel'

import Swal from 'sweetalert2'
import api from '../axios.js'

// Reactive data
const selectedCongress = ref('')
const selectedChamber = ref('Senate')
const committees = ref([])
const showModal = ref(false)
const isEditing = ref(false)
const saving = ref(false)
const loading = ref(false)
const formSubmitted = ref(false)

const filters = ref({
  global: { value: null, matchMode: 'contains' },
  name: { operator: 'and', constraints: [{ value: null, matchMode: 'startsWith' }] },
  jurisdiction: { operator: 'and', constraints: [{ value: null, matchMode: 'contains' }] },
  chairperson: { operator: 'and', constraints: [{ value: null, matchMode: 'contains' }] }
})

const form = ref({
  id: null,
  name: '',
  jurisdiction: '',
  chairperson: '',
  chamber: ''
})

const congressOptions = [
  { label: '20th Congress', value: '20' },
  { label: '19th Congress', value: '19' },
  { label: '18th Congress', value: '18' },
  { label: '17th Congress', value: '17' }
]

const chamberOptions = [
  { label: 'Senate', value: 'Senate' },
  { label: 'House of Representatives', value: 'House' }
]

// Computed properties
const filteredCommittees = computed(() => {
  return committees.value.filter(committee => committee.chamber === selectedChamber.value)
})

const senateCount = computed(() => {
  return committees.value.filter(c => c.chamber === 'Senate').length
})

const houseCount = computed(() => {
  return committees.value.filter(c => c.chamber === 'House').length
})

// Methods
const loadCommittees = async () => {
  try {
    loading.value = true
    const response = await api.get(`/committees?congress_no=${selectedCongress.value}`)
    committees.value = response.data
  } catch (error) {
    console.error('Error loading committees:', error)
  } finally {
    loading.value = false
  }
}

const selectChamber = (chamber) => {
  selectedChamber.value = chamber
}

const openAddModal = () => {
  isEditing.value = false
  form.value = { id: null, name: '', jurisdiction: '', chairperson: '', chamber: selectedChamber.value }
  showModal.value = true
}

const openEditModal = (committee) => {
  isEditing.value = true
  form.value = { ...committee }
  showModal.value = true
}

const closeModal = () => {
  showModal.value = false
  formSubmitted.value = false
  saving.value = false
  form.value = { id: null, name: '', jurisdiction: '', chairperson: '', chamber: 'Senate' }
}

const saveCommittee = async () => {
  formSubmitted.value = true
  
  if (!form.value.name || !form.value.chamber) {
    return
  }
  
  const result = await Swal.fire({
    title: 'Confirm Action',
    text: 'Confirming',
    icon: 'question',
    showCancelButton: true,
    confirmButtonText: 'Proceed',
    cancelButtonText: 'Cancel',
    target: document.body
  })
  
  if (!result.isConfirmed) {
    return
  }
  
  try {
    saving.value = true
    const data = {
      name: form.value.name,
      jurisdiction: form.value.jurisdiction,
      chairperson: form.value.chairperson,
      chamber: form.value.chamber,
      congress_no: selectedCongress.value
    }
    
    if (isEditing.value) {
      await api.put(`/committees/${form.value.id}`, data)
    } else {
      await api.post('/committees', data)
    }
    
    closeModal()
    loadCommittees()
    
    Swal.fire({
      title: 'Success!',
      text: `Committee ${isEditing.value ? 'updated' : 'created'} successfully`,
      icon: 'success',
      confirmButtonText: 'OK',
      target: document.body
    })
  } catch (error) {
    console.error('Error saving committee:', error)
  } finally {
    saving.value = false
  }
}

const deleteCommittee = async (id) => {
  const result = await Swal.fire({
    title: 'Delete Committee',
    text: 'Are you sure you want to delete this committee?',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Delete',
    cancelButtonText: 'Cancel',
    confirmButtonColor: '#ef4444',
    target: document.body
  })
  
  if (result.isConfirmed) {
    try {
      await api.delete(`/committees/${id}`)
      loadCommittees()
      
      Swal.fire({
        title: 'Deleted!',
        text: 'Committee has been deleted successfully',
        icon: 'success',
        confirmButtonText: 'OK',
        target: document.body
      })
    } catch (error) {
      console.error('Error deleting committee:', error)
    }
  }
}

const loadCurrentCongress = async () => {
  try {
    const response = await api.get('/current-congress')
    selectedCongress.value = response.data.congress_no
  } catch (error) {
    selectedCongress.value = '20' // fallback
  }
}

// Lifecycle
onMounted(async () => {
  await loadCurrentCongress()
  loadCommittees()
})
</script>

<style scoped>
.committees-page {
  padding: 2rem;
  /* max-width: 1200px; */
  margin: 0 auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #1e3a8a;
}

.page-header h1 {
  color: #1e3a8a;
  font-size: 2rem;
  font-weight: 700;
}

.header-controls {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.congress-selector {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.congress-selector label {
  font-weight: 600;
  color: #1e3a8a;
}

.chamber-tabs {
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.committee-name {
  font-weight: 600;
  color: #1e3a8a;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.table-header h3 {
  color: #1e3a8a;
  margin: 0;
}

.actions {
  display: flex;
  gap: 0.5rem;
}

.modal-form {
  padding: 0;
}

.form-grid {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding: 1.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group .p-inputtext,
.form-group .p-textarea,
.form-group .p-dropdown,
.form-group .p-select {
  width: 100% !important;
  box-sizing: border-box;
}

.form-group .p-select .p-select-label {
  width: 100% !important;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1.5rem;
  border-top: 1px solid #e5e7eb;
  background: #f8fafc;
}

@media (max-width: 768px) {
  .committees-page {
    padding: 1rem;
  }
  
  .page-header {
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
  }
  
  .chamber-tabs {
    flex-direction: column;
  }
}
</style>